import { useEffect, useState } from "react";
import axios from "axios";
import { itemsToExclude } from "../../utils/mappings";
import { useModalStack } from "../../utils/modalContext";
import { fetchItems } from "../../utils/fetchItemData";

const ExpCalculator = ({ items }) => {
  // Load initial state from localStorage or use defaults  
    const { pushModal } = useModalStack();
    const [state, setState] = useState(() => {
    const saved = localStorage.getItem("expCalculatorState");
    return saved
      ? JSON.parse(saved)
      : {
          accountName: "",
          selectedSkill: "farming",
          currentLevel: "",
          currentExp: "",
          targetLevel: "",
          expNeeded: null,
          error: "",
          percentToNextLevel: null,
          bestLevelItems: null,
          mostProfitItems: null,
        };
  });

  const [levelsData, setLevelsData] = useState([]);
  const excludedItemsSet = new Set(itemsToExclude);
  const filteredItems = items?.filter(
    (item) =>
      !excludedItemsSet.has(item.id) &&
      ((item.crop && Object.keys(item.crop).length > 0) ||
        (item.produce && item.produce.length > 0) ||
        (item.recipes && item.recipes.length > 0) ||
        (item.craftingMap && item.craftingMap.length > 0))
  );

  // Save to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem("expCalculatorState", JSON.stringify(state));
  }, [state]);

  const skills = ["farming", "cooking", "forestry", "woodwork", "petcare", "mining", "stoneshaping", "metalworking", "business"];

  useEffect(() => {
    const fetchLevels = async () => {
      try {
        const res = await fetch("https://api.pixelore.wiki/api/levels");
        const data = await res.json();

        // Convert object to array
        const levelsArray = Object.entries(data).map(([skillName, details]) => ({
          skillName,
          ...details,
        }));

        setLevelsData(levelsArray);
      } catch (err) {
        console.error("Failed to fetch level data:", err);
        setState((prev) => ({ ...prev, error: "Failed to load level data" }));
      }
    };

    fetchLevels();
  }, []);

  const handleOpenModal = async (itemId) => {
      try {
        const itemData = await fetchItems({ itemId });
        if (!itemData || !itemData[0]) return;
  
        pushModal({
          id: `item-${itemId}`,
          type: "itemModal",
          props: {
            item: itemData[0],
            onClose: () => {},
            showNavigation: false,
            onNavigate: null,
            totalItems: 1,
            index: null,
          },
        });
      } catch (err) {
        console.error("Failed to fetch item:", err);
      }
    };

  const handleLookup = async () => {
    try {
      const tokenRes = await axios.get("https://api.pixelore.wiki/auth/get-auth-token");
      const token = tokenRes.data.token;

      const response = await axios.get("https://api.pixelore.wiki/api/playerEndpoint", {
        params: {
          username: state.accountName,
          detail: 5,
          quests: true,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.data.player;

      if (!data || !data.levels || data.levels[state.selectedSkill] === undefined) {
        setState((prev) => ({ ...prev, error: "Could not find EXP data for that account or skill." }));
        return;
      }

      const skillData = data.levels[state.selectedSkill];

      setState((prev) => ({
        ...prev,
        currentLevel: skillData.level,
        currentExp: skillData.exp.toFixed(2),
        error: "",
      }));
    } catch (err) {
      console.error("Error fetching account data:", err);
      setState((prev) => ({ ...prev, error: "Failed to look up account." }));
    }
  };

  const calculateExp = () => {
    setState((prev) => ({ ...prev, error: "" }));

    let level = parseInt(state.currentLevel, 10);
    const current = parseFloat(state.currentExp);
    const target = parseInt(state.targetLevel, 10);

    if (isNaN(current) || isNaN(target) || isNaN(level)) {
      setState((prev) => ({ ...prev, error: "Please enter valid numbers for all fields." }));
      return;
    }

    if (target < 1 || target > 100 || level < 0 || level >= target) {
      setState((prev) => ({
        ...prev,
        error: "Make sure your current level is below the target level and both are between 1 and 100.",
      }));
      return;
    }

    if (!levelsData.length || !levelsData[level] || !levelsData[target]) {
      setState((prev) => ({ ...prev, error: "Level data not loaded properly." }));
      return;
    }

    // Total exp needed between levels (excluding current partial progress)
    let totalNeeded = levelsData[target].exp - levelsData[level].exp - current;

    const percent = (current / (levelsData[target].exp - levelsData[level].exp)) * 100;

    const [bestLevelItems, mostProfitItems] = getBestItems();
    setState((prev) => ({
      ...prev,
      expNeeded: Math.ceil(totalNeeded),
      percentToNextLevel: percent,
      bestLevelItems,
      mostProfitItems,
    }));
  };

  // Helper function to update state
  const handleChange = (field, value) => {
    setState((prev) => ({ ...prev, [field]: value }));
  };

  const getBestItems = () => {
    let industryItems;
    let bestLevelItems;
    let mostProfitItems;

    switch (state.selectedSkill) {
      case "farming":
        industryItems = filteredItems
          ? filteredItems.filter((item) => {
              if (item.crop) {
                const cropLevel = Number(item.crop.plantingLvl);
                return !isNaN(cropLevel) && cropLevel <= state.currentLevel;
              }

              // Check crafting recipes
              if (item.craftingMap?.[0]?.allRecipes?.length) {
                return item.craftingMap[0].allRecipes.some((recipe) => recipe.expType === state.selectedSkill && Number(recipe.level) <= state.currentLevel);
              }

              return false;
            })
          : [];

        // Best EXP/Energy Items (handles both crops and recipes)
        bestLevelItems = industryItems
          .map((item) => {
            // Handle crops
            if (item.crop) {
              return {
                itemId: item.id,
                itemName: item.name,
                itemImage: item.image,
                expPerEnergy: item.crop.harvestExp / (-1 * item.crop.plantingEnergy + item.crop.harvestEnergy),
                type: "crop",
                level: Number(item.crop.plantingLvl),
              };
            }

            // Handle recipes
            if (item.craftingMap?.[0]?.allRecipes?.length) {
              const bestRecipe = item.craftingMap[0].allRecipes.reduce((best, current) => {
                const currentRatio = current.exp / current.energy;
                const bestRatio = best.exp / best.energy;
                return currentRatio > bestRatio ? current : best;
              });

              return {
                itemId: item.id,
                itemName: item.name,
                itemImage: item.image,
                expPerEnergy: bestRecipe.exp / bestRecipe.energy,
                type: "recipe",
                level: Number(bestRecipe.level),
              };
            }

            return null;
          })
          .filter(Boolean)
          .sort((a, b) => b.expPerEnergy - a.expPerEnergy)
          .slice(0, 7);

        // Most Profit Items (adjust calculation as needed)
        mostProfitItems = industryItems
          .map((item) => {
            // Handle crops
            if (item.crop) {
              // Find the result item (corrected filter)
              const resultItem = items.find((i) => i.id === item.crop.resultId);
              if (!resultItem) return null; // Skip if result item not found

              const itemMarketPrice = resultItem.baseMarketPrice || resultItem.marketPrice || 0;

              // Calculate total energy (planting + harvesting)
              const totalEnergy = Math.abs(item.crop.plantingEnergy) + item.crop.harvestEnergy;

              // Calculate profit per energy
              const profitPerEnergy = itemMarketPrice / totalEnergy;

              return {
                itemId: item.crop.resultId,
                itemName: item.crop.result,
                itemImage: resultItem.image,
                profit: null,
                profitPerEnergy,
                type: "crop",
                level: Number(item.crop.plantingLvl),
                marketPrice: itemMarketPrice, // Added for debugging
              };
            }

            // Handle recipes
            if (item.craftingMap?.[0]?.allRecipes?.length) {
              return {
                itemId: item.id,
                itemName: item.name,
                itemImage: item.image,
                profit: item.profit,
                profitPerEnergy: null,
                type: "recipe",
                level: Number(item.level),
              };
            }

            return null;
          })
          .filter(Boolean)
          .sort((a, b) => b.profitPerEnergy - a.profitPerEnergy)
          .slice(0, 7);
        break;

      case "forestry":
      case "mining":
      case "petcare":
        industryItems = filteredItems
          ? filteredItems.filter((item) => {
              if (!item.produce?.length) return false;
              return item.produce.some((product) => {
                if (product.expType !== state.selectedSkill) return false;
                const productLevel = Number(product.level);
                if (isNaN(productLevel)) return false;
                return productLevel <= state.currentLevel;
              });
            })
          : [];

        bestLevelItems = industryItems
          .filter((item) => item.produce?.length > 0) // Ensure produce exists
          .map((item) => {
            // Filter out excluded produce tiers if needed
            const validProduce = item.produce.filter(
              (product) => !excludedItemsSet.has(product._id) // Assuming _id is in excludedItemsSet
            );

            if (validProduce.length === 0) return null;

            // Find produce with highest exp/energy
            const bestProduct = validProduce.reduce((best, current) => {
              const currentRatio = current.exp / current.energy;
              const bestRatio = best.exp / best.energy;
              return currentRatio > bestRatio ? current : best;
            });

            return {
              itemId: item.id,
              itemName: item.name,
              itemImage: item.image,
              tier: bestProduct.tier, // Include tier
              level: bestProduct.level,
              expPerEnergy: bestProduct.energy ? bestProduct.exp / bestProduct.energy : 0,
              growTime: bestProduct.growTime, // Optional: Include growth time
            };
          })
          .filter(Boolean) // Remove nulls
          .sort((a, b) => b.expPerEnergy - a.expPerEnergy)
          .slice(0, 7);

        mostProfitItems = industryItems
          .filter((item) => item.produce?.length > 0 && item.baseMarketPrice) // Ensure price exists
          .map((item) => {
            const validProduce = item.produce.filter((product) => !excludedItemsSet.has(product._id));

            if (validProduce.length === 0) return null;

            // Find produce with highest profit/energy
            const bestProduct = validProduce.reduce((best, current) => {
              const currentProfitPerEnergy = item.baseMarketPrice / current.energy;
              const bestProfitPerEnergy = item.baseMarketPrice / best.energy;
              return currentProfitPerEnergy > bestProfitPerEnergy ? current : best;
            });

            return {
              itemId: item.id,
              itemName: item.name,
              itemImage: item.image,
              tier: bestProduct.tier || "No Tier",
              level: bestProduct.level,
              profitPerEnergy: bestProduct.energy ? item.baseMarketPrice / bestProduct.energy : item.baseMarketPrice,
              growTime: bestProduct.growTime,
            };
          })
          .filter(Boolean)
          .sort((a, b) => b.profitPerEnergy - a.profitPerEnergy)
          .slice(0, 7);
        break;

      case "cooking":
      case "woodwork":
      case "stoneshaping":
      case "metalworking":
      case "business":
        industryItems = filteredItems
          ? filteredItems.filter((item) =>
              item.craftingMap?.[0]?.allRecipes?.every((recipe) => recipe.expType === state.selectedSkill && recipe.level <= state.currentLevel)
            )
          : [];

        bestLevelItems = industryItems
          .map((item) => {
            // Filter out excluded recipes first, then find the best remaining one
            const validRecipes = item.craftingMap[0].allRecipes.filter(
              (recipe) => !excludedItemsSet.has(recipe.achId) // Assuming recipes have an 'id'
            );
            if (validRecipes.length === 0) return null; // Skip items with no valid recipes

            const bestRecipe = validRecipes.reduce((best, current) => {
              const currentRatio = current.exp / current.energy;
              const bestRatio = best.exp / best.energy;
              return currentRatio > bestRatio ? current : best;
            });

            return {
              itemId: item.id,
              itemName: item.name,
              itemImage: item.image,
              level: bestRecipe.level,
              expPerEnergy: bestRecipe.exp / bestRecipe.energy,
            };
          })
          .filter(Boolean) // Remove null entries (items with no valid recipes)
          .sort((a, b) => b.expPerEnergy - a.expPerEnergy)
          .slice(0, 7);

        mostProfitItems = industryItems
          .filter((item) => item.profit != null) // Keeps only items with non-null profit
          .map((item) => ({
            itemId: item.id,
            itemName: item.name,
            itemImage: item.image,
            profit: item.profit,
          }))
          .sort((a, b) => b.profit - a.profit)
          .slice(0, 7);

        break;

      default:
        console.log("No skills match");
        break;
    }
    console.log(industryItems);
    console.log(bestLevelItems, mostProfitItems);

    return [bestLevelItems, mostProfitItems];
  };

  return (
    <div className="calculator-style">
      <h3 className="calculator-title">EXP to Level Calculator</h3>
      <div className="space-y-6 divide-y">
        {/* Account lookup */}
        <div className="space-y-2">
          <p className="text-sm text-[var(--text-color)]">Look up EXP from your account:</p>
          <div className="text-sm flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              placeholder="Enter account name"
              value={state.accountName}
              onChange={(e) => handleChange("accountName", e.target.value)}
              className="calculator-form border px-3 py-1 rounded-md w-full"
            />

            <select
              value={state.selectedSkill}
              onChange={(e) => handleChange("selectedSkill", e.target.value)}
              className="calculator-form border px-3 py-2 rounded-md"
            >
              {skills.map((skill) => (
                <option key={skill} value={skill}>
                  {skill.charAt(0).toUpperCase() + skill.slice(1)}
                </option>
              ))}
            </select>
            <button onClick={handleLookup} className="panel-button">
              Lookup
            </button>
          </div>
        </div>

        {/* Manual entry */}
        <div className="flex flex-row gap-2 text-sm">
          <div>
            <p className="font-medium mb-1">Your Current Level</p>
            <input
              type="number"
              placeholder="e.g. 10"
              value={state.currentLevel}
              onChange={(e) => handleChange("currentLevel", e.target.value)}
              className="calculator-form border px-3 py-2 rounded-md w-full"
            />
          </div>

          <div>
            <p className="text-sm font-medium mb-1">Your Current EXP</p>
            <input
              type="number"
              placeholder="e.g. 12000"
              value={state.currentExp}
              onChange={(e) => handleChange("currentExp", e.target.value)}
              className="calculator-form border px-3 py-2 rounded-md w-full"
            />
          </div>

          <div>
            <p className="text-sm font-medium mb-1">Target Level</p>
            <input
              type="number"
              placeholder="e.g. 30"
              value={state.targetLevel}
              onChange={(e) => handleChange("targetLevel", e.target.value)}
              className="calculator-form border px-3 py-2 rounded-md w-full"
            />
          </div>

          <button onClick={calculateExp} className="panel-button">
            Calculate
          </button>
        </div>

        {/* Result or Error */}
        {state.error && <p className="text-orange-700 text-xs">{state.error}</p>}
        {state.expNeeded !== null && !state.error && (
          <div className="bg-[var(--panel-bg-color)] p-3 rounded-md text-sm">
            You need <strong>{state.expNeeded.toLocaleString()}</strong> EXP to reach level {state.targetLevel}.
          </div>
        )}

        {state.currentLevel !== null && state.percentToNextLevel !== null && (
          <div className="bg-[var(--panel-bg-color)] p-4 rounded-md shadow-md">
            <p className="text-sm mb-1">
              Current Level: <strong>{state.currentLevel}</strong>
            </p>
            <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
              <div className="bg-rose-400 h-4 rounded-full transition-all duration-300" style={{ width: `${state.percentToNextLevel}%` }}></div>
            </div>
            <p className="text-xs text-[var(--text3-color)]">
              {state.percentToNextLevel.toFixed(2)}% to level {state.targetLevel}
            </p>
          </div>
        )}

        {state.bestLevelItems && state.mostProfitItems && (
          <div className="calculator-project rounded-lg shadow-md p-4 sm:p-6 mb-6 flex flex-col md:flex-row gap-4">
            {/* Best Items to Level */}
            <div className="bg-[var(--panel-sections)] p-3 sm:p-4 rounded-md shadow-md flex-1 h-full flex flex-col">
              <h3 className="text-lg font-semibold mb-3 sm:mb-4">Best Items to Level <span className="text-xs">(Level {state.currentLevel} and under)</span></h3>
              <div className="space-y-2 sm:space-y-3 flex-1">
                {state.bestLevelItems.map((item) => (
                  <div
                    key={`best-${item.itemId}`} onClick={() => handleOpenModal(item.itemId)}
                    className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 border border-[var(--outline-color)] rounded-lg bg-[var(--entity-mini)] cursor-pointer hover:bg-[var(--entity-hover-bg-color)] hover:text-[var(--entity-hover-text-color)]"
                  >
                    <img src={item.itemImage} alt={item.itemName} className="w-8 h-8 sm:w-10 sm:h-10 object-contain" />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm sm:text-base truncate">{item.itemName}</h4>
                      <p className="text-xs sm:text-sm text-[var(--text3-color)]">EXP/Energy: {item.expPerEnergy?.toLocaleString()}</p>
                    </div>
                    <span className="text-xs bg-blue-500 text-white px-2 py-1 rounded whitespace-nowrap">Lvl {item.level}</span>
                    {item.tier && <span className="text-xs bg-green-500 text-white px-2 py-1 rounded whitespace-nowrap">Tier {item.tier}</span>}
                  </div>
                ))}
              </div>
            </div>

            {/* Most Profitable Items */}
            <div className="bg-[var(--panel-sections)] p-3 sm:p-4 rounded-md shadow-md flex-1 h-full flex flex-col">
              <h3 className="text-lg font-semibold mb-3 sm:mb-4">Most Profitable Items</h3>
              <div className="space-y-2 sm:space-y-3 flex-1">
                {state.mostProfitItems.map((item) => (
                  <div
                    key={`profit-${item.itemId}`}  onClick={() => handleOpenModal(item.itemId)}
                    className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 border border-[var(--outline-color)] rounded-lg bg-[var(--entity-mini)] cursor-pointer hover:bg-[var(--entity-hover-bg-color)] hover:text-[var(--entity-hover-text-color)]"
                  >
                    <img src={item.itemImage} alt={item.itemName} className="w-8 h-8 sm:w-10 sm:h-10 object-contain" />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm sm:text-base truncate">{item.itemName}</h4>
                      {item.profitPerEnergy ? (
                        <p className="text-xs sm:text-sm text-[var(--text3-color)]">Profit/Energy: {item.profitPerEnergy.toFixed(2)}</p>
                      ) : (
                        <p className="text-xs sm:text-sm text-[var(--text3-color)]">Profit: {item.profit?.toLocaleString()}</p>
                      )}
                    </div>
                    <span className="text-xs bg-green-500 text-white px-2 py-1 rounded whitespace-nowrap">Lvl {item.level || ""}</span>
                    {item.tier && <span className="text-xs bg-green-500 text-white px-2 py-1 rounded whitespace-nowrap">Tier {item.tier}</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpCalculator;
